import { useEffect, useRef, type ReactNode } from "react";
import { ClipboardPen, Phone, X } from "lucide-react";
import type { Case } from "@/lib/useRealtimeCases";
import type { CallGuide } from "@/lib/callScript";
import { euros, getCallScriptContext } from "@/lib/callScript";
import {
  parisDateTimeToUtcIso,
  toParisDateInputValue,
  toParisTimeInputValue,
} from "@/lib/parisDateTime";
import { MultiSelectDropdown } from "../ui/MultiSelectDropdown";
import { ACTIVITIES_OPTIONS, EXTRAS_OPTIONS } from "@/lib/caseOptions";

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none";
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}
function Say({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 text-sm leading-relaxed text-gray-800">
      « {children} »
    </p>
  );
}

export function CallScriptDrawer({
  caseData,
  salesperson,
  quoteAmount,
  guide,
  onGuideChange,
  onFieldsChange,
  onClose,
  onNotes,
  onRecap,
  recapBusy,
  recapMessage,
  loading,
  error,
  disabled = false,
  onRetry,
}: {
  caseData: Case;
  salesperson: string;
  quoteAmount: number | null;
  guide: CallGuide;
  onGuideChange: (patch: Partial<CallGuide>) => void;
  onFieldsChange: (patch: Partial<Case>, key: string) => void;
  onClose: () => void;
  onNotes: () => void;
  onRecap: () => void;
  recapBusy: boolean;
  recapMessage: string;
  loading: boolean;
  error: boolean;
  disabled?: boolean;
  onRetry: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const context = getCallScriptContext(caseData, salesperson);
  const askBudget = !context.budget || guide.budget_confirmation !== "confirmed";
  useEffect(() => {
    heading.current?.focus();
  }, []);
  const setCase = (patch: Partial<Case>) => onFieldsChange(patch, "call-script");
  const setDate = (date: string) =>
    setCase(
      date
        ? {
            event_date: parisDateTimeToUtcIso(
              date,
              toParisTimeInputValue(caseData.event_date) || "00:00",
            ),
          }
        : { event_date: null, event_time_provided: false },
    );
  return (
    <aside
      id="case-call-script"
      aria-labelledby="call-script-title"
      className="case-call-script pointer-events-auto flex min-h-0 flex-col border-r border-gray-200 bg-white shadow-xl"
    >
      <div className="shrink-0 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h2
            ref={heading}
            tabIndex={-1}
            id="call-script-title"
            className="text-base font-semibold text-gray-900 outline-none"
          >
            Script d’appel
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le script"
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>
        <p className="truncate text-xs text-gray-500">
          {caseData.client_first_name || caseData.client_last_name
            ? `${caseData.client_first_name || ""} ${caseData.client_last_name || ""}`.trim()
            : "Contact à renseigner"}
          {caseData.company_name ? ` · ${caseData.company_name}` : ""}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {caseData.client_phone && (
            <a
              href={`tel:${caseData.client_phone.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:underline"
            >
              <Phone size={13} />
              {caseData.client_phone}
            </a>
          )}
          {quoteAmount != null && (
            <span
              className={`rounded px-2 py-1 text-xs ${quoteAmount > 1500 ? "bg-amber-100 text-amber-900" : "bg-gray-100 text-gray-600"}`}
            >
              {quoteAmount > 1500 ? "Appel à prioriser · " : "Devis · "}
              {euros(quoteAmount)} HT
            </span>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {loading && (
          <p role="status" className="mb-3 text-sm text-gray-500">
            Chargement du suivi d’appel…
          </p>
        )}
        {error && (
          <div role="alert" className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
            Le suivi d’appel n’a pas pu être chargé.{" "}
            <button type="button" onClick={onRetry} className="font-medium underline">
              Réessayer
            </button>
          </div>
        )}
        <fieldset
          disabled={loading || error || disabled}
          className="min-w-0 space-y-8 disabled:opacity-60"
        >
          <legend className="sr-only">Guide d’appel</legend>
          <section aria-labelledby="script-introduction" className="space-y-4">
            <h3 id="script-introduction" className="font-semibold text-gray-900">
              1. Accroche et 3 oui
            </h3>
            <Say>{context.greeting}</Say>
            <Say>{context.introduction}</Say>
            <Say>{context.confirmation}</Say>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Participants">
                <input
                  aria-label="Participants dans le script"
                  type="number"
                  min="0"
                  step="1"
                  value={context.count ?? ""}
                  onChange={(e) => {
                    const count = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                    setCase({ participants: count, pax: count });
                  }}
                  className={inputClass}
                />
              </Field>
              <Field label="Date de l’événement">
                <input
                  type="date"
                  value={toParisDateInputValue(caseData.event_date)}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>
            <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={guide.three_yes_validated}
                onChange={(e) => onGuideChange({ three_yes_validated: e.target.checked })}
                className="mt-0.5 accent-indigo-600"
              />
              3 oui validés
            </label>
            {context.budget ? (
              <>
                <p className="text-xs font-medium text-gray-500">
                  Budget indicatif du dossier : {context.budget}
                </p>
                <Say>
                  Vous aviez indiqué une enveloppe indicative de {context.budget}, c’est toujours la
                  base qu’on vise ou on l’ajuste selon le programme ?
                </Say>
                <fieldset className="space-y-2">
                  <legend className="mb-2 text-sm font-medium text-gray-700">
                    Confirmation du budget
                  </legend>
                  {(
                    [
                      ["confirmed", "Confirmé tel quel"],
                      ["adjusted", "Ajusté pendant l’appel"],
                      ["not_applicable", "Non applicable"],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="call-budget-confirmation"
                        value={value}
                        checked={guide.budget_confirmation === value}
                        onChange={() => onGuideChange({ budget_confirmation: value })}
                        className="accent-indigo-600"
                      />
                      {label}
                    </label>
                  ))}
                </fieldset>
              </>
            ) : (
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                Budget à préciser dans la partie 3 ci-dessous.
              </p>
            )}
          </section>
          <section
            aria-labelledby="script-project"
            className="space-y-4 border-t border-gray-200 pt-6"
          >
            <h3 id="script-project" className="font-semibold text-gray-900">
              2. Découverte et recommandation
            </h3>
            <Say>
              Quel est l’objectif principal de votre événement : remercier, fêter, renforcer la
              cohésion ou intégrer de nouveaux collaborateurs ?
            </Say>
            <Field label="Objectif de l’événement">
              <input
                value={guide.event_goal || ""}
                onChange={(e) => onGuideChange({ event_goal: e.target.value || null })}
                maxLength={4000}
                className={inputClass}
              />
            </Field>
            <Field label="Priorité du client">
              <select
                value={guide.client_driver || ""}
                onChange={(e) =>
                  onGuideChange({
                    client_driver: (e.target.value || null) as CallGuide["client_driver"],
                  })
                }
                className={inputClass}
              >
                <option value="">À préciser</option>
                <option value="image">Image</option>
                <option value="cohesion">Cohésion</option>
                <option value="fun">Convivialité / fun</option>
                <option value="budget">Budget</option>
              </select>
            </Field>
            <Say>
              On part sur une compétition ou un challenge entreprise, ou plutôt sur de la détente
              libre ? Souhaitez-vous un espace privatisé pour votre groupe ?
            </Say>
            <MultiSelectDropdown
              label="Activités souhaitées"
              value={caseData.activities || []}
              options={ACTIVITIES_OPTIONS}
              onChange={(activities) => setCase({ activities })}
            />
            <MultiSelectDropdown
              label="Espaces et extras"
              value={caseData.extras || []}
              options={EXTRAS_OPTIONS}
              onChange={(extras) => setCase({ extras })}
            />
            <Say>
              Pour la restauration et les boissons, qu’imaginez-vous : planches et cocktails, ou un
              repas assis ? Quel format de boissons conviendrait à votre groupe ?
            </Say>
            <Field label="Notes restauration et boissons">
              <textarea
                rows={3}
                value={guide.catering_notes || ""}
                maxLength={12000}
                onChange={(e) => onGuideChange({ catering_notes: e.target.value || null })}
                className={inputClass}
              />
            </Field>
            <p className="text-xs leading-relaxed text-gray-500">
              Adapter la recommandation aux offres, aux capacités et aux conditions du centre.
              Vérifier leur disponibilité avant de s’engager.
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={guide.expert_recommendation}
                onChange={(e) => onGuideChange({ expert_recommendation: e.target.checked })}
                className="accent-indigo-600"
              />
              Recommandation adaptée proposée
            </label>
          </section>
          <section
            aria-labelledby="script-decision"
            className="space-y-4 border-t border-gray-200 pt-6"
          >
            <h3 id="script-decision" className="font-semibold text-gray-900">
              3. Budget, décideur et calendrier
            </h3>
            {askBudget ? (
              <Say>
                Pour composer votre proposition sur mesure, quel est le budget plafond par personne
                ou au total à ne pas dépasser ?
              </Say>
            ) : (
              <p className="rounded-lg bg-green-50 p-3 text-sm text-green-800">
                Budget confirmé : {context.budget}. Il reste modifiable ci-dessous.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Budget total (€)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={caseData.budget ?? ""}
                  onChange={(e) =>
                    setCase({ budget: e.target.value ? Math.max(0, Number(e.target.value)) : null })
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Par participant (€)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={caseData.budget_per_person ?? ""}
                  onChange={(e) =>
                    setCase({
                      budget_per_person: e.target.value
                        ? Math.max(0, parseInt(e.target.value, 10))
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </Field>
            </div>
            <p className="text-xs text-gray-500">
              Montants indicatifs du dossier. Modifier les participants ne recalcule pas
              automatiquement le budget ni le devis.
            </p>
            <Say>
              Êtes-vous le seul à valider, ou présenterez-vous la proposition à votre direction ou à
              votre CSE ?
            </Say>
            <Field label="Décideur — nom et rôle">
              <input
                value={guide.decision_maker || ""}
                maxLength={1000}
                onChange={(e) => onGuideChange({ decision_maker: e.target.value || null })}
                className={inputClass}
              />
            </Field>
            <Say>Quand souhaitez-vous arrêter votre choix final ?</Say>
            <Field label="Date de décision souhaitée">
              <input
                type="date"
                value={guide.decision_date || ""}
                onChange={(e) => onGuideChange({ decision_date: e.target.value || null })}
                className={inputClass}
              />
            </Field>
          </section>
          <section
            aria-labelledby="script-follow-up"
            className="space-y-4 border-t border-gray-200 pt-6"
          >
            <h3 id="script-follow-up" className="font-semibold text-gray-900">
              4. Reformulation et prochaine étape
            </h3>
            <Say>{context.closing}</Say>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date de relance">
                <input
                  type="date"
                  value={caseData.phone_reminder_date || ""}
                  onChange={(e) =>
                    setCase(
                      e.target.value
                        ? { phone_reminder_date: e.target.value }
                        : { phone_reminder_date: null, phone_reminder_time: null },
                    )
                  }
                  className={inputClass}
                />
              </Field>
              <Field label="Heure (Paris)">
                <input
                  type="time"
                  value={caseData.phone_reminder_time?.slice(0, 5) || ""}
                  disabled={!caseData.phone_reminder_date}
                  onChange={(e) => setCase({ phone_reminder_time: e.target.value || null })}
                  className={inputClass}
                />
              </Field>
            </div>
            <Say>{context.followUp}</Say>
            <p className="text-xs text-gray-500">
              La date alimente les actions « À faire ». L’heure est le rendez-vous convenu, sans
              déclencher de message.
            </p>
            {(!guide.three_yes_validated ||
              !caseData.phone_reminder_date ||
              !caseData.phone_reminder_time) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">À compléter avant de terminer l’appel</p>
                <ul className="mt-1 list-disc pl-4">
                  {!guide.three_yes_validated && <li>Valider les 3 oui.</li>}
                  {(!caseData.phone_reminder_date || !caseData.phone_reminder_time) && (
                    <li>Convenir d’une date et d’une heure de relance.</li>
                  )}
                </ul>
              </div>
            )}
          </section>
        </fieldset>
      </div>
      <div className="shrink-0 space-y-2 border-t border-gray-200 bg-white px-4 py-3">
        <button type="button" onClick={onRecap} disabled={disabled || loading || error || recapBusy}
          className="w-full rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {recapBusy ? "Enregistrement du récapitulatif…" : "Ajouter le récap aux notes internes"}
        </button>
        {recapMessage && <p role="status" className="text-xs text-gray-700">{recapMessage}</p>}
        <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onNotes}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ClipboardPen size={16} />
          Notes et statut du dossier
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-2 text-sm text-gray-600 hover:bg-gray-100"
        >
          Replier
        </button>
        </div>
      </div>
    </aside>
  );
}
